import { useMemo, useState } from 'react'
import { Alert, Platform, Text, View } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { AppModal } from '@/components/ui/AppModal'
import { GlassSheet } from '@/components/ui/GlassSheet'
import { useTranslation } from 'react-i18next'
import { Check, CreditCard, Plus, ShieldCheck, Star, Trash2, X } from '@/components/ui/icons'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { Pressable } from '@/components/ui/Pressable'
import { Skeleton } from '@/components/ui/Skeleton'
import { TextInput } from '@/components/ui/TextInput'
import { CardForm } from '@/components/booking/CardForm'
import {
  useDeactivatePaymentMethod,
  usePaymentMethods,
  useSavePaymentMethod,
  useSetDefaultPaymentMethod,
} from '@/lib/hooks/use-payment'
import { useSemanticColors } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { fullProfileName } from '@/lib/utils/profile-display'
import { useAuth } from '@/lib/hooks/use-auth'
import { COLORS, FONTS, RADIUS, SPACING } from '@/lib/brand'
import { notification, NotificationType } from '@/lib/utils/haptics'
import type { CardPaymentData, PaymentMethod } from '@/lib/types/payment.types'

/** Height of one PaymentMethodRow. The loading skeleton stands in for exactly this. */
const PAYMENT_CARD_HEIGHT = 124

function formatExpiry(method: PaymentMethod) {
  if (!method.cardExpMonth || !method.cardExpYear) return '—'
  return `${String(method.cardExpMonth).padStart(2, '0')}/${String(method.cardExpYear).slice(-2)}`
}

function brandLabel(method: PaymentMethod, fallback: string) {
  if (!method.cardBrand || method.cardBrand === 'unknown') return fallback
  return method.cardBrand === 'mastercard' ? 'Mastercard' : method.cardBrand.toUpperCase()
}

export default function PaymentMethodsScreen() {
  const { profile } = useAuth()
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { data: methods = [], isLoading, isError, refetch } = usePaymentMethods()
  const saveMethod = useSavePaymentMethod()
  const setDefault = useSetDefaultPaymentMethod()
  const deactivate = useDeactivatePaymentMethod()

  const [addVisible, setAddVisible] = useState(false)
  const [label, setLabel] = useState('')
  const [makeDefault, setMakeDefault] = useState(true)
  const [webDeleteTarget, setWebDeleteTarget] = useState<PaymentMethod | null>(null)
  const isMutating = saveMethod.isPending || setDefault.isPending || deactivate.isPending

  const sortedMethods = useMemo(
    () =>
      [...methods].sort(
        (a, b) =>
          Number(b.isDefault) - Number(a.isDefault) || b.createdAt.localeCompare(a.createdAt),
      ),
    [methods],
  )

  const handleCloseSheet = () => {
    if (saveMethod.isPending) return
    setAddVisible(false)
    setLabel('')
    setMakeDefault(methods.length === 0)
    saveMethod.reset()
  }

  const handleOpenSheet = () => {
    setLabel('')
    setMakeDefault(methods.length === 0)
    saveMethod.reset()
    setAddVisible(true)
  }

  const handleSave = async (card: CardPaymentData) => {
    try {
      const result = await saveMethod.mutateAsync({
        card,
        label: label.trim() || undefined,
        makeDefault,
      })
      void notification(NotificationType.Success)
      handleCloseSheet()
      if (!result.isDefault && methods.length === 0) {
        await setDefault.mutateAsync(result.id)
      }
    } catch {
      void notification(NotificationType.Error)
    }
  }

  const deleteMethod = (method: PaymentMethod) => {
    deactivate.mutate(method.id, {
      onSuccess: () => {
        setWebDeleteTarget(null)
        void notification(NotificationType.Success)
      },
      onError: () => void notification(NotificationType.Error),
    })
  }

  const confirmDelete = (method: PaymentMethod) => {
    if (Platform.OS === 'web') {
      setWebDeleteTarget(method)
      return
    }

    Alert.alert(t('payment_methods.delete_title'), t('payment_methods.delete_desc'), [
      { text: t('common.cancel'), style: 'cancel' },
      { text: t('common.delete'), style: 'destructive', onPress: () => deleteMethod(method) },
    ])
  }

  return (
    <ScreenContainer scroll>
      <ScreenHeader
        title={t('payment_methods.title')}
        right={
          <Pressable
            onPress={handleOpenSheet}
            accessibilityRole="button"
            accessibilityLabel={t('payment_methods.add')}
            testID="payment-methods.add-button"
            style={{
              width: 44,
              height: 44,
              borderRadius: RADIUS.full,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: colors.surfaceRaised,
              borderWidth: 1,
              borderColor: colors.border,
            }}
          >
            <Plus size={20} color={colors.textPrimary} strokeWidth={2.2} />
          </Pressable>
        }
      />

      {/* `flexGrow` (not `flex`) so the column keeps its content height when
          there are cards to list and only claims the leftover space when
          there aren't — that leftover is what the empty state centres in. */}
      <View
        style={{
          flexGrow: 1,
          paddingHorizontal: SPACING.md,
          paddingBottom: SPACING.xl,
          gap: SPACING.md,
        }}
      >
        <TrustPanel />

        {isLoading ? (
          // Card-shaped placeholders, not a centred spinner: the skeleton has
          // the shape of what it replaces (STANDARDS §8), so the swap to real
          // cards moves nothing on the page.
          <View style={{ gap: SPACING.sm }} testID="payment-methods.skeleton">
            {[0, 1].map((index) => (
              <Skeleton
                key={index}
                width="100%"
                height={PAYMENT_CARD_HEIGHT}
                borderRadius={RADIUS.lg}
                staggerIndex={index}
              />
            ))}
          </View>
        ) : isError ? (
          <View style={{ gap: SPACING.sm }}>
            <Text
              style={{
                fontSize: 15,
                lineHeight: 22,
                fontFamily: FONTS.body,
                color: colors.textSecondary,
              }}
            >
              {t('payment_methods.load_error')}
            </Text>
            <Button title={t('common.retry')} variant="secondary" onPress={() => void refetch()} />
          </View>
        ) : sortedMethods.length === 0 ? (
          // The card used to hang off the info banner with the bottom half of
          // the display flat behind it. It owns that space now instead of
          // ignoring it.
          <View style={{ flexGrow: 1, justifyContent: 'center' }}>
            <View
              testID="payment-methods.empty"
              style={{
                padding: SPACING.lg,
                borderRadius: RADIUS.lg,
                borderWidth: 1,
                borderColor: colors.border,
                backgroundColor: colors.surface,
                gap: SPACING.md,
              }}
            >
              <CreditCard size={34} color={colors.textMuted} strokeWidth={1.5} />
              <View style={{ gap: SPACING.xs }}>
                <Text
                  style={{
                    fontSize: 20,
                    lineHeight: 25,
                    fontFamily: FONTS.bodyBold,
                    color: colors.textPrimary,
                  }}
                >
                  {t('payment_methods.empty_title')}
                </Text>
                <Text
                  style={{
                    fontSize: 14,
                    lineHeight: 21,
                    fontFamily: FONTS.body,
                    color: colors.textSecondary,
                  }}
                >
                  {t('payment_methods.empty_desc')}
                </Text>
              </View>
              <Button
                title={t('payment_methods.add_first')}
                variant="primary"
                onPress={handleOpenSheet}
              />
            </View>
          </View>
        ) : (
          <View style={{ gap: SPACING.sm }}>
            {sortedMethods.map((method) => (
              <PaymentMethodRow
                key={method.id}
                method={method}
                busy={isMutating}
                onSetDefault={() => {
                  setDefault.mutate(method.id, {
                    onSuccess: () => void notification(NotificationType.Success),
                    onError: () => void notification(NotificationType.Error),
                  })
                }}
                onDelete={() => confirmDelete(method)}
              />
            ))}
          </View>
        )}
      </View>

      <AppModal
        visible={addVisible}
        animationType="slide"
        transparent
        onRequestClose={handleCloseSheet}
      >
        <View
          style={{
            flex: 1,
            justifyContent: 'flex-end',
          }}
        >
          {/* EL MISMO CardForm DEL CHECKOUT, CON EL MISMO PROBLEMA.
              Esta hoja monta los mismos cuatro campos y el mismo boton de
              guardar al final, y no tenia ScrollView: en iOS el teclado
              numerico tapaba "Guardar" y no habia forma de bajarlo ni de
              desplazarse. Ademas su contenido mide ~750pt y la hoja topa en el
              92% de la pantalla, asi que en un telefono de 667pt se salia por
              abajo aunque no hubiera teclado.
              Un ScrollView con las mismas tres props que el checkout resuelve
              las dos: `automaticallyAdjustKeyboardInsets` es la contraparte de
              `adjustResize` que iOS no aplica por defecto, y
              `keyboardDismissMode` da la salida que el teclado numerico no
              tiene. */}
          <GlassSheet
            style={{
              maxHeight: '92%',
              borderTopLeftRadius: RADIUS.xl,
              borderTopRightRadius: RADIUS.xl,
              borderTopWidth: 1,
              borderColor: colors.border,
            }}
          >
            <ScrollView
              style={{ flexShrink: 1 }}
              contentContainerStyle={{
                padding: SPACING.md,
                paddingBottom: SPACING.xl,
                gap: SPACING.md,
              }}
              keyboardShouldPersistTaps="handled"
              automaticallyAdjustKeyboardInsets
              keyboardDismissMode="interactive"
              showsVerticalScrollIndicator={false}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.md }}>
                <View style={{ flex: 1 }}>
                  <Text
                    style={{
                      fontSize: 22,
                      lineHeight: 28,
                      fontFamily: FONTS.bodyBold,
                      color: colors.textPrimary,
                    }}
                  >
                    {t('payment_methods.add')}
                  </Text>
                  <Text
                    style={{
                      marginTop: 2,
                      fontSize: 13,
                      lineHeight: 18,
                      fontFamily: FONTS.body,
                      color: colors.textSecondary,
                    }}
                  >
                    {t('payment_methods.add_desc')}
                  </Text>
                </View>
                <Pressable
                  onPress={handleCloseSheet}
                  accessibilityRole="button"
                  accessibilityLabel={t('common.cancel')}
                  testID="payment-methods.close-add"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: RADIUS.full,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: colors.surface,
                    borderWidth: 1,
                    borderColor: colors.border,
                  }}
                >
                  <X size={18} color={colors.textPrimary} strokeWidth={2.2} />
                </Pressable>
              </View>

              <TextInput
                label={t('payment_methods.nickname')}
                placeholder={t('payment_methods.nickname_placeholder')}
                value={label}
                onChangeText={setLabel}
                autoCapitalize="words"
                maxLength={40}
                focusColor={COLORS.coral.DEFAULT}
                testID="payment-methods.nickname"
              />

              <Checkbox
                checked={makeDefault}
                onChange={setMakeDefault}
                label={t('payment_methods.make_default')}
                testID="payment-methods.make-default"
              />

              <CardForm
                amountCents={0}
                defaultCardholderName={fullProfileName(profile)}
                onSubmit={handleSave}
                loading={saveMethod.isPending}
                disabled={saveMethod.isPending}
                submitTitle={t('payment_methods.save_card')}
                footer={
                  <Text
                    style={{
                      fontSize: 12,
                      lineHeight: 17,
                      fontFamily: FONTS.body,
                      color: colors.textMuted,
                    }}
                  >
                    {t('payment_methods.secure_footnote')}
                  </Text>
                }
              />

              {saveMethod.error ? (
                <Text
                  style={{
                    fontSize: 13,
                    lineHeight: 18,
                    fontFamily: FONTS.body,
                    color: colors.textError,
                  }}
                  testID="payment-methods.save-error"
                >
                  {saveMethod.error.message}
                </Text>
              ) : null}
            </ScrollView>
          </GlassSheet>
        </View>
      </AppModal>

      <AppModal
        visible={Platform.OS === 'web' && webDeleteTarget !== null}
        animationType="fade"
        transparent
        onRequestClose={() => setWebDeleteTarget(null)}
      >
        <View
          style={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            padding: SPACING.md,
          }}
        >
          <GlassSheet
            accessibilityViewIsModal
            accessibilityLabel={t('payment_methods.delete_title')}
            style={{
              width: '100%',
              maxWidth: 420,
              borderRadius: RADIUS.xl,
              borderWidth: 1,
              borderColor: colors.border,
            }}
            contentStyle={{
              padding: SPACING.lg,
              gap: SPACING.md,
            }}
          >
            <View style={{ gap: SPACING.xs }}>
              <Text
                style={{
                  fontSize: 20,
                  lineHeight: 25,
                  fontFamily: FONTS.bodyBold,
                  color: colors.textPrimary,
                }}
              >
                {t('payment_methods.delete_title')}
              </Text>
              <Text
                style={{
                  fontSize: 14,
                  lineHeight: 21,
                  fontFamily: FONTS.body,
                  color: colors.textSecondary,
                }}
              >
                {t('payment_methods.delete_desc')}
              </Text>
            </View>
            <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: SPACING.sm }}>
              <Button
                title={t('common.cancel')}
                variant="secondary"
                onPress={() => setWebDeleteTarget(null)}
                disabled={deactivate.isPending}
              />
              <Button
                title={t('common.delete')}
                variant="primary"
                loading={deactivate.isPending}
                onPress={() => {
                  if (webDeleteTarget) deleteMethod(webDeleteTarget)
                }}
              />
            </View>
          </GlassSheet>
        </View>
      </AppModal>
    </ScreenContainer>
  )
}

function TrustPanel() {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  return (
    <View
      testID="payment-methods.trust-panel"
      style={{
        flexDirection: 'row',
        gap: SPACING.sm,
        padding: SPACING.md,
        borderRadius: RADIUS.lg,
        borderWidth: 1,
        borderColor: colors.border,
        backgroundColor: colors.surfaceRaised,
      }}
    >
      <View
        style={{
          width: 42,
          height: 42,
          borderRadius: RADIUS.full,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: `${COLORS.coral.DEFAULT}12`,
        }}
      >
        <ShieldCheck size={22} color={COLORS.coral.DEFAULT} strokeWidth={2.2} />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          style={{
            fontSize: 16,
            lineHeight: 21,
            fontFamily: FONTS.bodyBold,
            color: colors.textPrimary,
          }}
        >
          {t('payment_methods.trust_title')}
        </Text>
        <Text
          style={{
            marginTop: 3,
            fontSize: 13,
            lineHeight: 19,
            fontFamily: FONTS.body,
            color: colors.textSecondary,
          }}
        >
          {t('payment_methods.trust_body')}
        </Text>
      </View>
    </View>
  )
}

function PaymentMethodRow({
  method,
  busy,
  onSetDefault,
  onDelete,
}: {
  method: PaymentMethod
  busy: boolean
  onSetDefault: () => void
  onDelete: () => void
}) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  return (
    <View
      testID={`payment-methods.card-${method.id}`}
      style={{
        padding: SPACING.md,
        borderRadius: RADIUS.lg,
        borderWidth: 1,
        borderColor: method.isDefault ? `${COLORS.coral.DEFAULT}55` : colors.border,
        backgroundColor: colors.surface,
        gap: SPACING.md,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.md }}>
        <View
          style={{
            width: 46,
            height: 46,
            borderRadius: RADIUS.md,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: colors.surfaceRaised,
            borderWidth: 1,
            borderColor: colors.border,
          }}
        >
          <CreditCard size={22} color={colors.textPrimary} strokeWidth={1.8} />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.xs }}>
            <Text
              numberOfLines={1}
              style={{
                flexShrink: 1,
                fontSize: 16,
                lineHeight: 21,
                fontFamily: FONTS.bodyBold,
                color: colors.textPrimary,
              }}
            >
              {method.label}
            </Text>
            {method.isDefault ? (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 3,
                  paddingHorizontal: SPACING.xs,
                  paddingVertical: 3,
                  borderRadius: RADIUS.full,
                  backgroundColor: `${COLORS.coral.DEFAULT}12`,
                }}
              >
                <Star size={11} color={COLORS.coral.DEFAULT} fill={COLORS.coral.DEFAULT} />
                <Text
                  style={{
                    fontSize: 12,
                    lineHeight: 16,
                    fontFamily: FONTS.bodyBold,
                    color: colors.textAccent,
                  }}
                >
                  {t('payment_methods.default_badge')}
                </Text>
              </View>
            ) : null}
          </View>
          <Text
            numberOfLines={1}
            style={{
              marginTop: 2,
              fontSize: 13,
              lineHeight: 18,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
            }}
          >
            {brandLabel(method, t('payment_methods.card'))} •••• {method.cardLast4 ?? '----'} ·{' '}
            {t('payment_methods.expiry', { expiry: formatExpiry(method) })}
          </Text>
        </View>
      </View>

      <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
        <Pressable
          disabled={busy || method.isDefault}
          onPress={onSetDefault}
          accessibilityRole="button"
          accessibilityLabel={t('payment_methods.set_default')}
          testID={`payment-methods.set-default-${method.id}`}
          style={{
            minHeight: 40,
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: SPACING.xs,
            borderRadius: RADIUS.md,
            borderWidth: 1,
            borderColor: colors.border,
            opacity: method.isDefault ? 0.55 : 1,
          }}
        >
          <Check size={15} color={colors.textPrimary} strokeWidth={2.2} />
          <Text
            style={{
              fontSize: 13,
              fontFamily: FONTS.bodyBold,
              color: colors.textPrimary,
            }}
          >
            {method.isDefault
              ? t('payment_methods.default_badge')
              : t('payment_methods.set_default')}
          </Text>
        </Pressable>
        <Pressable
          disabled={busy}
          onPress={onDelete}
          accessibilityRole="button"
          accessibilityLabel={t('common.delete')}
          testID={`payment-methods.delete-${method.id}`}
          style={{
            width: 44,
            height: 40,
            borderRadius: RADIUS.md,
            alignItems: 'center',
            justifyContent: 'center',
            borderWidth: 1,
            borderColor: colors.border,
          }}
        >
          <Trash2 size={17} color={colors.textMuted} strokeWidth={2} />
        </Pressable>
      </View>
    </View>
  )
}
